import { useCallback, useEffect, useState } from 'react';

export interface MediaControlsState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  muted: boolean;
}

const DEFAULT_STATE: MediaControlsState = {
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  muted: false,
};

// External consumers go through `useMediaPlayer` — this hook subscribes to
// `timeupdate` and would re-render the comment tree at ~20Hz.
export function useMediaElementControls(el: HTMLVideoElement | null) {
  const [state, setState] = useState<MediaControlsState>(DEFAULT_STATE);

  useEffect(() => {
    if (!el) {
      setState(DEFAULT_STATE);
      return;
    }

    const onTimeUpdate = () =>
      setState((s) =>
        Math.abs(s.currentTime - el.currentTime) < 0.05 ? s : { ...s, currentTime: el.currentTime },
      );
    const onMeta = () =>
      setState((s) => ({ ...s, duration: Number.isFinite(el.duration) ? el.duration : 0 }));
    const onPlay = () => setState((s) => ({ ...s, isPlaying: true }));
    const onPause = () => setState((s) => ({ ...s, isPlaying: false }));
    const onEnded = () => setState((s) => ({ ...s, isPlaying: false }));
    const onVolume = () => setState((s) => ({ ...s, muted: el.muted }));

    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('volumechange', onVolume);

    setState({
      isPlaying: !el.paused,
      currentTime: el.currentTime,
      duration: Number.isFinite(el.duration) ? el.duration : 0,
      muted: el.muted,
    });

    return () => {
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('volumechange', onVolume);
    };
  }, [el]);

  const togglePlay = useCallback(() => {
    if (!el) return;
    if (el.paused) void el.play();
    else el.pause();
  }, [el]);

  const setProgressPercent = useCallback(
    (percent: number) => {
      if (!el || !Number.isFinite(el.duration) || el.duration <= 0) return;
      el.currentTime = (percent / 100) * el.duration;
    },
    [el],
  );

  const toggleMute = useCallback(() => {
    if (el) el.muted = !el.muted;
  }, [el]);

  return { state, togglePlay, setProgressPercent, toggleMute };
}
